import { QRCode } from './qrCode';

/** How many light modules surround the symbol. Four is the quiet zone the standard asks for. */
const BORDER = 4;

/**
 * The dark modules as one SVG path, in a coordinate space of one unit per
 * module. One path rather than a rect per module keeps the file small and the
 * printed edges sharp.
 */
export function qrCodePathData(qr: QRCode): string {
  const parts: string[] = [];
  qr.modules.forEach((row, y) => {
    row.forEach((isDark, x) => {
      if (isDark) {
        parts.push(`M${x + BORDER} ${y + BORDER}h1v1h-1z`);
      }
    });
  });
  return parts.join('');
}

/** The side of the drawing, quiet zone included. */
export function qrCodeViewBoxSize(qr: QRCode): number {
  return qr.size + BORDER * 2;
}

/** A standalone SVG file, for the download that goes on a poster. */
export function qrCodeToSvgString(qr: QRCode, dark = '#000000', light = '#ffffff'): string {
  const side = qrCodeViewBoxSize(qr);
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${side} ${side}" width="${side * 10}" height="${side * 10}" shape-rendering="crispEdges">`,
    `<rect width="${side}" height="${side}" fill="${light}"/>`,
    `<path d="${qrCodePathData(qr)}" fill="${dark}"/>`,
    '</svg>',
  ].join('');
}

/** Paints the symbol on a canvas and returns it as a PNG data URL. */
export function qrCodeToPngDataUrl(qr: QRCode, pixelsPerModule = 12, dark = '#000000', light = '#ffffff'): string {
  const side = qrCodeViewBoxSize(qr) * pixelsPerModule;
  const canvas = document.createElement('canvas');
  canvas.width = side;
  canvas.height = side;
  const context = canvas.getContext('2d');
  if (context === null) {
    throw new Error('Canvas is not available');
  }
  context.fillStyle = light;
  context.fillRect(0, 0, side, side);
  context.fillStyle = dark;
  qr.modules.forEach((row, y) => {
    row.forEach((isDark, x) => {
      if (isDark) {
        context.fillRect(
          (x + BORDER) * pixelsPerModule,
          (y + BORDER) * pixelsPerModule,
          pixelsPerModule,
          pixelsPerModule
        );
      }
    });
  });
  return canvas.toDataURL('image/png');
}

/** Hands the browser a file to save, the same way the CSV export does. */
export function downloadFile(filename: string, href: string, revoke = false): void {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  link.click();
  if (revoke) {
    URL.revokeObjectURL(href);
  }
}

export function downloadQRCodeSvg(qr: QRCode, filename: string): void {
  const blob = new Blob([qrCodeToSvgString(qr)], { type: 'image/svg+xml;charset=utf-8' });
  downloadFile(filename, URL.createObjectURL(blob), true);
}

export function downloadQRCodePng(qr: QRCode, filename: string): void {
  downloadFile(filename, qrCodeToPngDataUrl(qr));
}
